import { ReactNode } from 'react';
import { Dialog as ChakraDialogNamespace, DialogRootProps, HStack, Portal } from '@chakra-ui/react';

import { CloseButton } from '../CloseButton';
import { LEGACY_MODE, LegacyMode, ModernMode, useMode } from '../Mode';

interface DialogContentProps extends ChakraDialogNamespace.ContentProps {
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement>;
  backdrop?: boolean;
  ref?: React.RefObject<HTMLDivElement | null>;
}

export function DialogContent({ ref, ...props }: DialogContentProps) {
  const { children, portalled = true, portalRef, backdrop = true, ...rest } = props;
  const { mode } = useMode();
  return (
    <Portal disabled={!portalled} container={portalRef}>
      {backdrop && <ChakraDialogNamespace.Backdrop />}
      <ChakraDialogNamespace.Positioner>
        {mode === LEGACY_MODE ? (
          <LegacyMode>
            <ChakraDialogNamespace.Content ref={ref} {...rest} asChild={false}>
              {children}
            </ChakraDialogNamespace.Content>
          </LegacyMode>
        ) : (
          <ModernMode>
            <ChakraDialogNamespace.Content ref={ref} {...rest} asChild={false}>
              {children}
            </ChakraDialogNamespace.Content>
          </ModernMode>
        )}
      </ChakraDialogNamespace.Positioner>
    </Portal>
  );
}

export interface CloseTriggerProps extends ChakraDialogNamespace.CloseTriggerProps {
  ref?: React.RefObject<HTMLButtonElement | null>;
}
export function DialogCloseTrigger({ ref, ...props }: CloseTriggerProps) {
  return (
    <ChakraDialogNamespace.CloseTrigger position="absolute" top="2" insetEnd="2" {...props} asChild>
      <CloseButton size="sm" ref={ref} />
    </ChakraDialogNamespace.CloseTrigger>
  );
}

export const DialogRoot = ChakraDialogNamespace.Root;
export const DialogFooter = ChakraDialogNamespace.Footer;

export interface DialogHeaderProps extends ChakraDialogNamespace.HeaderProps {
  ref?: React.RefObject<HTMLDivElement | null>;
}
export function DialogTitle({ ref, ...props }: DialogHeaderProps) {
  return <ChakraDialogNamespace.Title as="h2" {...props} ref={ref} />;
}
export const DialogBody = ChakraDialogNamespace.Body;
export const DialogBackdrop = ChakraDialogNamespace.Backdrop;
export const DialogHeader = ChakraDialogNamespace.Header;
export const DialogDescription = ChakraDialogNamespace.Description;
export const DialogTrigger = ChakraDialogNamespace.Trigger;
export const DialogActionTrigger = ChakraDialogNamespace.ActionTrigger;

export interface DialogProps extends Omit<DialogRootProps, 'children'> {
  /**
   * The title for the dialog
   */
  title: ReactNode;
  /**
   * The body content for the dialog
   */
  body: ReactNode;
  /**
   * Actions to show in the footer of the dialog.
   * Will be wrapped in a stack with spacing, so you can just use a fragment for multiple buttons
   */
  actions?: ReactNode;
}

export const Dialog = ({ body, actions, title, open, onOpenChange, ...restModalProps }: DialogProps) => (
  <DialogRoot open={open} onOpenChange={onOpenChange} placement="center" {...restModalProps}>
    <DialogBackdrop />
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
      </DialogHeader>
      <DialogCloseTrigger />
      <DialogBody>{body}</DialogBody>
      {actions && (
        <DialogFooter>
          <HStack gap={2} width="100%" justifyContent="flex-end">
            {actions}
          </HStack>
        </DialogFooter>
      )}
    </DialogContent>
  </DialogRoot>
);

export const AlertDialog = (props: DialogProps) => <Dialog {...props} role="alertdialog" />;
